/**
 * Metadata paneli (Tasarım V4, Öneri 13; V5-03).
 *
 * Detay ekranında özet/tagline artık karar alanında (hero) gösterilir. Bu
 * panel yalnızca "Hakkında" ayrıntılarını (puan, tür, oyuncu, koleksiyon)
 * taşır. Yönetici eylemleri kullanıcı içeriğinden ayrı bir açılır bölümde
 * durur; normal kullanıcı eşleşme güveni gibi iç terimleri görmez.
 *
 * Yönetici bölümünde ayrıca:
 *   - afiş/backdrop yükleme ve kaldırma (klasördeki yan dosya bilgisiyle),
 *   - sağlayıcı verisini ezmeyen manuel künye formu bulunur.
 */
import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import type { Item, ItemMetadata, MetadataOverrideInput } from '../api.ts';
import { InfoIcon, RefreshIcon, SparklesIcon, TrashIcon } from '../Icon.tsx';
import { mediaUrl } from '../mediaToken.ts';

type ArtworkKind = 'poster' | 'backdrop';
type ArtworkSource = 'upload' | 'sidecar' | null;

const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;

export function MetadataPanel({
  metadata,
  enabled,
  isAdmin,
  canMatch,
  busy,
  error,
  posterUrl = null,
  backdropUrl = null,
  artwork = null,
  onRefresh,
  onMatch,
  onClear,
  onSave,
  onUploadArtwork,
  onRemoveArtwork,
}: {
  metadata: ItemMetadata | null;
  enabled: boolean;
  isAdmin: boolean;
  /** Sezon/bölümde manuel eşleştirme gizlenir; kök dizi üzerinden yapılır. */
  canMatch: boolean;
  busy: boolean;
  error: string | null;
  posterUrl?: string | null;
  backdropUrl?: string | null;
  artwork?: Item['artwork'];
  onRefresh: () => void;
  onMatch: () => void;
  onClear: () => void;
  /** Verilmezse düzenleme formu gizlenir (mevcut testler/istemciler). */
  onSave?: (patch: MetadataOverrideInput) => Promise<void>;
  onUploadArtwork?: (kind: ArtworkKind, file: File) => Promise<void>;
  onRemoveArtwork?: (kind: ArtworkKind) => Promise<void>;
}): ReactNode {
  // Metadata yoksa normal kullanıcıya boş "Hakkında" bölümü gösterme.
  // Sağlayıcı kapalı olsa bile elle girilmiş künye **görünür**.
  if (!isAdmin && metadata === null) return null;

  const confidence = metadata === null ? 0 : Math.round(metadata.confidence * 100);

  return (
    <section className="section metadata-panel" aria-labelledby="metadata-title">
      <div className="section__header">
        <div className="section__header-title">
          <span className="section__header-icon">
            <SparklesIcon size={17} />
          </span>
          <h2 id="metadata-title" className="section__title">
            {metadata === null ? 'Metadata' : 'Hakkında'}
          </h2>
        </div>
        {isAdmin && metadata !== null && (
          <span
            className={`badge ${metadata.status === 'matched' ? 'badge--watched' : 'badge--warn'}`}
          >
            {metadata.hasOverride
              ? 'Elle düzenlendi'
              : metadata.status === 'matched'
                ? `Eşleşti · %${confidence}`
                : `Onay bekliyor · %${confidence}`}
          </span>
        )}
      </div>

      {!enabled && isAdmin && (
        <p className="metadata-panel__hint">
          <InfoIcon size={15} />
          Metadata sağlayıcısı yapılandırılmadı. Sunucuda
          <code> PBFORK_TMDB_API_KEY </code> tanımlayın veya aşağıdan elle künye girin.
        </p>
      )}

      {enabled && metadata === null && isAdmin && (
        <p className="metadata-panel__hint">Bu içerik için metadata bulunamadı.</p>
      )}

      {metadata !== null && <MetadataBody metadata={metadata} />}

      {error !== null && (
        <p className="metadata-panel__error" role="alert">
          {error}
        </p>
      )}

      {isAdmin && enabled && (
        <details className="metadata-panel__admin">
          <summary>Metadata yönetimi</summary>
          <div className="metadata-panel__actions">
            <button
              type="button"
              className="button button--ghost"
              onClick={onRefresh}
              disabled={busy}
            >
              <RefreshIcon size={14} />
              Yenile
            </button>
            {canMatch && (
              <button
                type="button"
                className="button button--ghost"
                onClick={onMatch}
                disabled={busy}
              >
                <SparklesIcon size={14} />
                Eşleştir
              </button>
            )}
            {metadata !== null && (
              <button
                type="button"
                className="button button--ghost metadata-panel__clear"
                onClick={onClear}
                disabled={busy}
              >
                <TrashIcon size={14} />
                Kaldır
              </button>
            )}
          </div>
        </details>
      )}

      {isAdmin && canMatch && (onSave !== undefined || onUploadArtwork !== undefined) && (
        <details className="metadata-panel__admin">
          <summary>Künyeyi ve görselleri düzenle</summary>

          {onUploadArtwork !== undefined && (
            <ArtworkEditor
              artwork={artwork}
              posterUrl={posterUrl}
              backdropUrl={backdropUrl}
              busy={busy}
              onUpload={onUploadArtwork}
              {...(onRemoveArtwork === undefined ? {} : { onRemove: onRemoveArtwork })}
            />
          )}

          {onSave !== undefined && (
            <MetadataEditor metadata={metadata} busy={busy} onSave={onSave} />
          )}
        </details>
      )}
    </section>
  );
}

const SOURCE_LABEL: Record<Exclude<ArtworkSource, null>, string> = {
  upload: 'Arayüzden yüklendi',
  sidecar: 'Klasörden',
};

function ArtworkEditor({
  artwork,
  posterUrl,
  backdropUrl,
  busy,
  onUpload,
  onRemove,
}: {
  artwork: Item['artwork'];
  posterUrl: string | null;
  backdropUrl: string | null;
  busy: boolean;
  onUpload: (kind: ArtworkKind, file: File) => Promise<void>;
  onRemove?: (kind: ArtworkKind) => Promise<void>;
}): ReactNode {
  const [uploading, setUploading] = useState<ArtworkKind | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const upload = async (kind: ArtworkKind, file: File | undefined): Promise<void> => {
    if (file === undefined || uploading !== null) return;
    setMessage(null);
    if (!file.type.startsWith('image/')) {
      setMessage('Yalnız görsel dosyası seçin.');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setMessage('Görsel en fazla 8 MB olabilir.');
      return;
    }
    setUploading(kind);
    try {
      await onUpload(kind, file);
      setMessage(kind === 'poster' ? 'Afiş güncellendi.' : 'Arka plan güncellendi.');
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : 'Görsel yüklenemedi.');
    } finally {
      setUploading(null);
    }
  };

  const remove = async (kind: ArtworkKind): Promise<void> => {
    if (onRemove === undefined || uploading !== null) return;
    setMessage(null);
    setUploading(kind);
    try {
      await onRemove(kind);
      setMessage('Yüklenen görsel kaldırıldı.');
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : 'Görsel kaldırılamadı.');
    } finally {
      setUploading(null);
    }
  };

  return (
    <div className="metadata-artwork">
      <p className="metadata-panel__hint">
        <InfoIcon size={15} />
        Klasöre <code>poster.jpg</code> / <code>folder.jpg</code> / <code>cover.jpg</code> veya
        <code> fanart.jpg</code> koyabilirsiniz. Buradaki yükleme klasördeki dosyayı geçersiz kılar.
      </p>

      <div className="metadata-artwork__grid">
        <ArtworkSlot
          kind="poster"
          label="Afiş"
          url={posterUrl}
          source={artwork?.posterSource ?? null}
          busy={busy || uploading !== null}
          onFile={(file) => void upload('poster', file)}
          {...(onRemove === undefined ? {} : { onRemove: () => void remove('poster') })}
        />
        <ArtworkSlot
          kind="backdrop"
          label="Arka plan"
          url={backdropUrl}
          source={artwork?.backdropSource ?? null}
          busy={busy || uploading !== null}
          onFile={(file) => void upload('backdrop', file)}
          {...(onRemove === undefined ? {} : { onRemove: () => void remove('backdrop') })}
        />
      </div>

      {message !== null && (
        <p className="metadata-panel__hint" role="status">
          {message}
        </p>
      )}
    </div>
  );
}

function ArtworkSlot({
  kind,
  label,
  url,
  source,
  busy,
  onFile,
  onRemove,
}: {
  kind: ArtworkKind;
  label: string;
  url: string | null;
  source: ArtworkSource;
  busy: boolean;
  onFile: (file: File | undefined) => void;
  onRemove?: () => void;
}): ReactNode {
  const inputId = `artwork-${kind}`;
  return (
    <div className={`metadata-artwork__slot metadata-artwork__slot--${kind}`}>
      <div className="metadata-artwork__preview">
        {url === null ? (
          <span className="metadata-artwork__empty">Görsel yok</span>
        ) : (
          <img src={mediaUrl(url)} alt={`${label} önizlemesi`} loading="lazy" />
        )}
      </div>
      <div className="metadata-artwork__meta">
        <strong>{label}</strong>
        {source !== null && <span className="badge">{SOURCE_LABEL[source]}</span>}
        <label className="button button--ghost metadata-artwork__upload" htmlFor={inputId}>
          {busy ? 'İşleniyor…' : 'Görsel seç'}
        </label>
        <input
          id={inputId}
          className="metadata-artwork__input"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          disabled={busy}
          onChange={(event) => {
            onFile(event.currentTarget.files?.[0]);
            // Aynı dosyayı yeniden seçebilmek için girdiyi sıfırla.
            event.currentTarget.value = '';
          }}
        />
        {source === 'upload' && onRemove !== undefined && (
          <button
            type="button"
            className="button button--ghost metadata-panel__clear"
            disabled={busy}
            onClick={onRemove}
          >
            <TrashIcon size={13} />
            Yüklemeyi kaldır
          </button>
        )}
      </div>
    </div>
  );
}

function MetadataEditor({
  metadata,
  busy,
  onSave,
}: {
  metadata: ItemMetadata | null;
  busy: boolean;
  onSave: (patch: MetadataOverrideInput) => Promise<void>;
}): ReactNode {
  const [title, setTitle] = useState('');
  const [year, setYear] = useState('');
  const [overview, setOverview] = useState('');
  const [tagline, setTagline] = useState('');
  const [releaseDate, setReleaseDate] = useState('');
  const [runtime, setRuntime] = useState('');
  const [genres, setGenres] = useState('');
  const [cast, setCast] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    setTitle(metadata?.title ?? '');
    setYear(metadata?.year === null || metadata?.year === undefined ? '' : String(metadata.year));
    setOverview(metadata?.overview ?? '');
    setTagline(metadata?.tagline ?? '');
    setReleaseDate(metadata?.releaseDate ?? '');
    setRuntime(
      metadata?.runtimeMinutes === null || metadata?.runtimeMinutes === undefined
        ? ''
        : String(metadata.runtimeMinutes),
    );
    setGenres(metadata?.genres.join(', ') ?? '');
    setCast(metadata?.cast.join(', ') ?? '');
  }, [metadata]);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setMessage(null);
    const parsedYear = Number.parseInt(year, 10);
    const parsedRuntime = Number.parseInt(runtime, 10);
    try {
      await onSave({
        title: title.trim() === '' ? null : title.trim(),
        year: Number.isFinite(parsedYear) ? parsedYear : null,
        overview: overview.trim() === '' ? null : overview.trim(),
        tagline: tagline.trim() === '' ? null : tagline.trim(),
        releaseDate: releaseDate.trim() === '' ? null : releaseDate.trim(),
        runtimeMinutes: Number.isFinite(parsedRuntime) ? parsedRuntime : null,
        genres: splitList(genres),
        cast: splitList(cast),
      });
      setMessage('Künye kaydedildi.');
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : 'Künye kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const disabled = busy || saving;
  return (
    <form className="metadata-edit" onSubmit={(event) => void submit(event)}>
      <div className="metadata-edit__grid">
        <label className="metadata-edit__field">
          <span>Başlık</span>
          <input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={300} />
        </label>
        <label className="metadata-edit__field metadata-edit__field--short">
          <span>Yıl</span>
          <input
            value={year}
            onChange={(event) => setYear(event.target.value)}
            inputMode="numeric"
            maxLength={4}
            placeholder="2024"
          />
        </label>
        <label className="metadata-edit__field metadata-edit__field--short">
          <span>Süre (dk)</span>
          <input
            value={runtime}
            onChange={(event) => setRuntime(event.target.value)}
            inputMode="numeric"
            maxLength={5}
          />
        </label>
        <label className="metadata-edit__field metadata-edit__field--short">
          <span>Çıkış tarihi</span>
          <input
            value={releaseDate}
            onChange={(event) => setReleaseDate(event.target.value)}
            placeholder="2024-05-01"
            maxLength={10}
          />
        </label>
        <label className="metadata-edit__field metadata-edit__field--wide">
          <span>Slogan</span>
          <input
            value={tagline}
            onChange={(event) => setTagline(event.target.value)}
            maxLength={500}
          />
        </label>
        <label className="metadata-edit__field metadata-edit__field--wide">
          <span>Özet</span>
          <textarea
            value={overview}
            onChange={(event) => setOverview(event.target.value)}
            rows={4}
            maxLength={10_000}
          />
        </label>
        <label className="metadata-edit__field metadata-edit__field--wide">
          <span>Türler (virgülle)</span>
          <input
            value={genres}
            onChange={(event) => setGenres(event.target.value)}
            placeholder="Dram, Gerilim"
          />
        </label>
        <label className="metadata-edit__field metadata-edit__field--wide">
          <span>Oyuncular (virgülle)</span>
          <input
            value={cast}
            onChange={(event) => setCast(event.target.value)}
            placeholder="Oyuncu A, Oyuncu B"
          />
        </label>
      </div>

      <div className="metadata-panel__actions">
        <button type="submit" className="button button--primary" disabled={disabled}>
          {saving ? 'Kaydediliyor…' : 'Künyeyi kaydet'}
        </button>
        <span className="metadata-panel__hint">
          Boş alanlar sağlayıcı değerine döner; <strong>Yenile</strong> düzenlemelerinizi silmez.
        </span>
      </div>

      {message !== null && (
        <p className="metadata-panel__hint" role="status">
          {message}
        </p>
      )}
    </form>
  );
}

function splitList(value: string): string[] {
  return value
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/** Metadata gövdesi — özet/tagline hero'da; burada doğrulanabilir künye. */
function MetadataBody({ metadata }: { metadata: ItemMetadata }): ReactNode {
  return (
    <>
      <div className="metadata-panel__facts">
        {metadata.rating !== null && <span>TMDB {metadata.rating.toFixed(1)}</span>}
        {metadata.imdbRating !== null && (
          <span
            title={
              metadata.imdbVotes === null
                ? undefined
                : `${metadata.imdbVotes.toLocaleString('tr-TR')} IMDb oyu`
            }
          >
            IMDb {metadata.imdbRating.toFixed(1)}
          </span>
        )}
        {metadata.votes !== null && <span>{metadata.votes.toLocaleString('tr-TR')} oy</span>}
        {metadata.runtimeMinutes !== null && <span>{metadata.runtimeMinutes} dk</span>}
        {metadata.releaseDate !== null && <span>{metadata.releaseDate.slice(0, 4)}</span>}
      </div>

      {metadata.genres.length > 0 && (
        <div className="metadata-panel__genres">
          {metadata.genres.map((genre) => (
            <span className="badge" key={genre}>
              {genre}
            </span>
          ))}
        </div>
      )}

      {metadata.cast.length > 0 && (
        <p className="metadata-panel__cast">
          <strong>Oyuncular:</strong> {metadata.cast.join(', ')}
        </p>
      )}

      {metadata.collection !== null && (
        <p className="metadata-panel__collection">
          <strong>Koleksiyon:</strong> {metadata.collection.name}
        </p>
      )}

      <p className="metadata-panel__attribution">
        Bu ürün TMDB API’sini kullanır.
        {metadata.imdbRating !== null && ' IMDb puanı OMDb API üzerinden alınır.'}
      </p>
    </>
  );
}
